import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, Option } from '../services/api'
import { RefreshCw, TrendingUp, Edit2, X, Check, ArrowUp, ArrowDown } from 'lucide-react'
import { useState } from 'react'

// Formatting functions
function formatIV(value: number | string | null): string {
  if (value === null || value === undefined) return '-'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '-'
  return `${Math.round(num * 100)}%`
}

function formatIVError(error: string | null): string {
  if (!error) return '-'
  switch (error) {
    case 'below_intrinsic':
      return '< intrinsic'
    case 'no_converge':
      return 'no converge'
    case 'expired':
      return 'expired'
    case 'no_price':
      return 'no price'
    default:
      return error
  }
}

function formatDelta(value: number | string | null): string {
  if (value === null || value === undefined) return '-'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '-'
  return num.toFixed(2)
}

function formatDivYield(value: number | string | null): string {
  if (value === null || value === undefined) return '-'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '-'
  return `${(num * 100).toFixed(1)}%`
}

function formatPrice(value: number | string | null): string {
  if (value === null || value === undefined) return '-'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '-'
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString()
}

// Sorting utilities
type SortDirection = 'asc' | 'desc' | null
type SortConfig = { key: string; direction: SortDirection }

function SortableHeader({
  label,
  sortKey,
  sortConfig,
  onSort,
  className = '',
}: {
  label: string
  sortKey: string
  sortConfig: SortConfig
  onSort: (key: string) => void
  className?: string
}) {
  const isActive = sortConfig.key === sortKey && sortConfig.direction !== null
  return (
    <th
      className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <div className={`flex items-center gap-1 ${className.includes('text-right') ? 'justify-end' : className.includes('text-center') ? 'justify-center' : ''}`}>
        {label}
        <span className="w-4 h-4 inline-flex items-center justify-center">
          {isActive && sortConfig.direction === 'asc' && <ArrowUp className="h-3 w-3" />}
          {isActive && sortConfig.direction === 'desc' && <ArrowDown className="h-3 w-3" />}
        </span>
      </div>
    </th>
  )
}

function sortOptions(data: Option[], sortConfig: SortConfig): Option[] {
  if (!sortConfig.direction) return data

  return [...data].sort((a, b) => {
    const aVal = (a as unknown as Record<string, unknown>)[sortConfig.key]
    const bVal = (b as unknown as Record<string, unknown>)[sortConfig.key]

    if (aVal == null && bVal == null) return 0
    if (aVal == null) return sortConfig.direction === 'asc' ? 1 : -1
    if (bVal == null) return sortConfig.direction === 'asc' ? -1 : 1

    let comparison = 0
    const aNum = typeof aVal === 'number' ? aVal : (typeof aVal === 'string' ? parseFloat(aVal) : NaN)
    const bNum = typeof bVal === 'number' ? bVal : (typeof bVal === 'string' ? parseFloat(bVal) : NaN)

    if (!isNaN(aNum) && !isNaN(bNum)) {
      comparison = aNum - bNum
    } else {
      comparison = String(aVal).localeCompare(String(bVal))
    }

    return sortConfig.direction === 'asc' ? comparison : -comparison
  })
}

export default function Options() {
  const queryClient = useQueryClient()
  const [editingOption, setEditingOption] = useState<string | null>(null)
  const [editDividendYield, setEditDividendYield] = useState<string>('')
  const [showExpired, setShowExpired] = useState(false)
  const [filterUnderlying, setFilterUnderlying] = useState<string>('')
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'underlying', direction: 'asc' })

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === null) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key, direction: null }
    })
  }

  const { data: optionsData, isLoading } = useQuery({
    queryKey: ['options', showExpired, filterUnderlying],
    queryFn: () => api.getOptions({
      expired: showExpired,
      underlying: filterUnderlying || undefined
    }),
  })

  const { data: underlyingsData } = useQuery({
    queryKey: ['underlyings'],
    queryFn: () => api.getUnderlyings(),
  })

  const { data: snapshots } = useQuery({
    queryKey: ['snapshots'],
    queryFn: () => api.getSnapshots(0, 1),
  })

  const latestUploadId = snapshots?.uploads?.[0]?.id

  const refreshMutation = useMutation({
    mutationFn: (uploadId: number) => api.refreshOptions(uploadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['options'] })
      queryClient.invalidateQueries({ queryKey: ['portfolio'] })
      queryClient.invalidateQueries({ queryKey: ['aggregate'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ occSymbol, dividendYield }: { occSymbol: string; dividendYield: number }) =>
      api.updateOption(occSymbol, { dividend_yield: dividendYield }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['options'] })
      setEditingOption(null)
    },
  })

  const handleStartEdit = (option: Option) => {
    setEditingOption(option.occ_symbol)
    setEditDividendYield(option.dividend_yield ? (option.dividend_yield * 100).toString() : '0')
  }

  const handleSaveEdit = (occSymbol: string) => {
    const dividendYield = parseFloat(editDividendYield) / 100
    if (!isNaN(dividendYield)) {
      updateMutation.mutate({ occSymbol, dividendYield })
    }
  }

  const handleCancelEdit = () => {
    setEditingOption(null)
    setEditDividendYield('')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const options = sortOptions(optionsData?.options || [], sortConfig)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Options</h1>
        <button
          onClick={() => latestUploadId && refreshMutation.mutate(latestUploadId)}
          disabled={refreshMutation.isPending || !latestUploadId}
          className="btn btn-primary inline-flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
          {refreshMutation.isPending ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>

      {/* Refresh Results */}
      {refreshMutation.isSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-semibold text-green-900 mb-2">Refresh Complete</h3>
          <ul className="text-sm text-green-800 space-y-1">
            <li>Options extracted: {refreshMutation.data.options_extracted}</li>
            <li>Underlying prices updated: {refreshMutation.data.prices_updated}</li>
            <li>Greeks calculated: {refreshMutation.data.greeks_calculated}</li>
            <li>Position exposures updated: {refreshMutation.data.exposures_updated}</li>
          </ul>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="showExpired"
              checked={showExpired}
              onChange={(e) => setShowExpired(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="showExpired" className="text-sm text-gray-700">Show Expired</label>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700">Underlying:</label>
            <select
              value={filterUnderlying}
              onChange={(e) => setFilterUnderlying(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">All</option>
              {underlyingsData?.underlyings?.map(u => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-500">
            {options.length} option{options.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Options Table */}
      <div className="card overflow-hidden">
        {options.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <SortableHeader label="Symbol" sortKey="symbol" sortConfig={sortConfig} onSort={handleSort} className="text-left" />
                  <SortableHeader label="Underlying" sortKey="underlying" sortConfig={sortConfig} onSort={handleSort} className="text-left" />
                  <SortableHeader label="Expiration" sortKey="expiration" sortConfig={sortConfig} onSort={handleSort} className="text-left" />
                  <SortableHeader label="Strike" sortKey="strike_price" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
                  <SortableHeader label="Type" sortKey="call_put" sortConfig={sortConfig} onSort={handleSort} className="text-center" />
                  <SortableHeader label="Ccy" sortKey="currency" sortConfig={sortConfig} onSort={handleSort} className="text-center" />
                  <SortableHeader label="Price" sortKey="price" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
                  <SortableHeader label="Undl Price" sortKey="underlying_price" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
                  <SortableHeader label="Delta" sortKey="delta" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
                  <SortableHeader label="IV" sortKey="implied_volatility" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
                  <SortableHeader label="Div Yield" sortKey="dividend_yield" sortConfig={sortConfig} onSort={handleSort} className="text-right" />
                  <SortableHeader label="Updated" sortKey="delta_iv_timestamp" sortConfig={sortConfig} onSort={handleSort} className="text-left" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {options.map((option) => (
                  <tr key={option.occ_symbol} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900 font-mono text-sm">{option.symbol}</p>
                        <p className="text-xs text-gray-400 font-mono">{option.occ_symbol}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium">{option.underlying}</td>
                    <td className="px-4 py-3 text-sm">{formatDate(option.expiration)}</td>
                    <td className="px-4 py-3 text-right font-mono">{formatPrice(option.strike_price)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 text-xs rounded font-medium ${
                        option.call_put === 'C'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {option.call_put === 'C' ? 'Call' : 'Put'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-600">{option.currency || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono">{formatPrice(option.price)}</td>
                    <td className="px-4 py-3 text-right font-mono">{formatPrice(option.underlying_price)}</td>
                    <td className={`px-4 py-3 text-right font-mono ${option.delta_estimated ? 'text-red-600' : ''}`}>
                      {formatDelta(option.delta)}
                      {option.delta_estimated && <span className="text-xs ml-1" title="Estimated using fallback method">*</span>}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono ${option.iv_error ? 'text-red-600' : ''}`}>
                      {option.iv_error ? formatIVError(option.iv_error) : formatIV(option.implied_volatility)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {editingOption === option.occ_symbol ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={editDividendYield}
                            onChange={(e) => setEditDividendYield(e.target.value)}
                            className="w-16 px-2 py-1 text-xs border rounded"
                            step="0.1"
                          />
                          <span className="text-xs text-gray-500">%</span>
                          <button
                            onClick={() => handleSaveEdit(option.occ_symbol)}
                            className="p-1 text-green-600 hover:bg-green-50 rounded"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <span className="font-mono">{formatDivYield(option.dividend_yield)}</span>
                          <button
                            onClick={() => handleStartEdit(option)}
                            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          >
                            <Edit2 className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {option.delta_iv_timestamp
                        ? new Date(option.delta_iv_timestamp).toLocaleString()
                        : '-'
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No options found.</p>
            <p className="text-sm text-gray-400">
              Options are automatically extracted when you upload a portfolio statement.
            </p>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="card bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-2">About Options Data</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>- Options are automatically extracted from uploaded IBKR statements</li>
          <li>- Click "Refresh Data" to fetch underlying prices and recalculate Greeks</li>
          <li>- Dividend yield can be manually edited for each option (affects IV/Delta calculations)</li>
          <li>- Delta and IV are calculated using Black-Scholes with Treasury rates</li>
          <li>- Nominal and delta-adjusted exposures appear in the Portfolio positions view</li>
        </ul>
      </div>
    </div>
  )
}
